<script lang="ts">
  import { createEventDispatcher } from "svelte";

  export let tone: "warning" | "info" = "warning";
  export let icon: string;
  export let title: string;
  export let detail: string = "";
  export let hint: string = "";
  export let testid: string;
  export let closeLabel: string = "Close";
  export let dismissible: boolean = true;
  export let dismissed: boolean = false;
  // FIX B: when `false`, the `×` performs no cosmetic local collapse; instead the
  // caller owns the behavior via `on:dismiss` (e.g. trigger real recovery, or
  // the caller hides the `×` entirely because there is no valid manual escape).
  // Default `true` keeps every existing caller's collapse-to-pill behavior.
  export let collapseOnDismiss: boolean = true;

  const dispatch = createEventDispatcher<{ dismiss: void }>();

  function onDismiss(): void {
    if (collapseOnDismiss) dismissed = true;
    dispatch("dismiss");
  }
</script>

{#if dismissible && dismissed}
  <button
    type="button"
    class="banner-pill banner-pill-{tone}"
    data-testid={`${testid}-pill`}
    title={title}
    aria-label={title}
    on:click={() => (dismissed = false)}>{icon}</button>
{:else}
  <div class="banner banner-{tone}" data-testid={`${testid}-badge`} role="alert">
    <span class="banner-icon" aria-hidden="true">{icon}</span>
    <span class="banner-text">
      <strong>{title}</strong>
      {#if detail}<span>{detail}</span>{/if}
      {#if hint}<span class="banner-hint">{hint}</span>{/if}
    </span>
    <slot />
    {#if dismissible}
      <button
        type="button"
        class="banner-close"
        data-testid={`${testid}-dismiss`}
        title={closeLabel}
        aria-label={closeLabel}
        on:click={onDismiss}>×</button>
    {/if}
  </div>
{/if}

<style>
  .banner {
    position: fixed;
    top: calc(env(safe-area-inset-top) + 0.75rem);
    left: 50%;
    transform: translateX(-50%);
    z-index: 80;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    max-width: min(94vw, 560px);
    color: #fff;
    padding: 0.6rem 1rem;
    border-radius: 10px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  }

  .banner-warning {
    background: #7c2d12;
    border: 1px solid #f59e0b;
  }

  .banner-info {
    background: #0b3a67;
    border: 1px solid #2563eb;
  }

  .banner-icon {
    font-size: 1.3rem;
    line-height: 1;
  }

  .banner-text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    font-size: 0.85rem;
  }

  .banner-text strong {
    font-size: 0.95rem;
  }

  .banner-hint {
    opacity: 0.85;
    font-size: 0.8rem;
  }

  .banner-close {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    padding: 0 0.25rem;
    opacity: 0.85;
    align-self: flex-start;
  }

  .banner-close:hover {
    opacity: 1;
  }

  .banner-pill {
    position: fixed;
    top: calc(env(safe-area-inset-top) + 0.75rem);
    left: 50%;
    transform: translateX(-50%);
    z-index: 80;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    font-size: 1.1rem;
    border-radius: 999px;
    color: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    cursor: pointer;
  }

  .banner-pill-warning {
    background: #7c2d12;
    border: 1px solid #f59e0b;
  }

  .banner-pill-info {
    background: #0b3a67;
    border: 1px solid #2563eb;
  }
</style>
